"use client"

// The primary affiliate CTA inside a DecisionBlock's actions slot — a filled-green
// "View on Amazon" chip for the product that side recommends. Sits at the decision
// peak ("Pick A if…"). Fires affiliate_link_clicked with the `compare-verdict`
// placement. Design ref: Relevance AI "Why it's #1" + CTA adjacency.

import { contentAnalytics, trackEvent } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import type { ProductRecommendationLink } from "@/lib/product-picks/types"

// Shared surface for the impression/click pair so `compare-verdict` CTR is
// computable. The legacy affiliate_link_clicked call below stays untouched.
const VERDICT_SURFACE = "compare-verdict"

function IconArrow({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  )
}

export function DecisionBuyChip({
  product,
  slug,
  variant,
}: {
  product: ProductRecommendationLink
  slug: string
  variant: "a" | "b"
}) {
  // Hook stays above the early return so the call order is unconditional. When
  // there is no affiliate URL the ref never attaches, so nothing fires.
  const impressionRef = useAffiliateImpression(VERDICT_SURFACE, {
    article_slug: `compare:${slug}`,
    product_name: product.name,
    affiliate_link: product.affiliateUrl ?? "",
    asin: product.asin ?? undefined,
    placement: VERDICT_SURFACE,
    source_context: `verdict-${variant}`,
  })

  if (!product.affiliateUrl) return null

  const handleClick = () => {
    trackEvent("affiliate_link_clicked", {
      article_slug: `compare:${slug}`,
      product_name: product.name,
      affiliate_link: product.affiliateUrl ?? "",
      placement: "compare-verdict",
      asin: product.asin ?? undefined,
      source_context: `verdict-${variant}`,
    })
    // Unified click on the SAME surface as the impression above.
    contentAnalytics.affiliateProductClicked(VERDICT_SURFACE, {
      productName: product.name,
      affiliateLink: product.affiliateUrl ?? "",
      asin: product.asin ?? undefined,
      article_slug: `compare:${slug}`,
      placement: VERDICT_SURFACE,
      source_context: `verdict-${variant}`,
    })
  }

  return (
    <a
      ref={impressionRef}
      href={product.affiliateUrl}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      data-affiliate="true"
      onClick={handleClick}
      className="cmp-buy"
    >
      View Price on Amazon
      <IconArrow size={11} />
    </a>
  )
}
